import { useCallback, useEffect, useState } from 'react'

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void
      execute: (siteKey: string, options: { action: string }) => Promise<string>
    }
  }
}

function loadRecaptchaScript(): Promise<void> {
  if (!SITE_KEY) return Promise.resolve()
  if (document.querySelector('script[data-recaptcha]')) {
    return new Promise((resolve, reject) => {
      let settled = false
      const finish = (fn: () => void) => {
        if (settled) return
        settled = true
        fn()
      }
      const waitReady = () => {
        if (window.grecaptcha) {
          window.grecaptcha.ready(() => finish(() => resolve()))
          return
        }
        window.setTimeout(waitReady, 50)
      }
      window.setTimeout(() => finish(() => reject(new Error('reCAPTCHA indisponível'))), 8000)
      waitReady()
    })
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`
    script.async = true
    script.dataset.recaptcha = 'true'
    script.onload = () => {
      window.grecaptcha?.ready(() => resolve())
    }
    script.onerror = () => reject(new Error('Falha ao carregar reCAPTCHA'))
    document.head.appendChild(script)
  })
}

export function useRecaptcha() {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!SITE_KEY) return
    loadRecaptchaScript()
      .then(() => setReady(true))
      .catch(() => setReady(false))
  }, [])

  const executeRecaptcha = useCallback(async (action = 'login'): Promise<string | undefined> => {
    if (!SITE_KEY) return undefined
    await loadRecaptchaScript()
    if (!window.grecaptcha) {
      throw new Error('reCAPTCHA indisponível')
    }
    return new Promise((resolve, reject) => {
      window.grecaptcha!.ready(async () => {
        try {
          const token = await window.grecaptcha!.execute(SITE_KEY, { action })
          resolve(token)
        } catch (e) {
          reject(e)
        }
      })
    })
  }, [])

  return { ready, executeRecaptcha, enabled: Boolean(SITE_KEY) }
}
